import React from 'react';
import axios from 'axios';
import { Types } from 'utils/constants';
import { ThemeContext } from 'contexts/theme/theme-context';


export const UserDataContext = React.createContext();

// we use a separate context for user data so that it can be changed without triggering a change to the user object.
export const UserDataProvider = ({ data, children }) => {
  const { setTheme } = React.useContext(ThemeContext);
  const [state, setState] = React.useState({ userData: null });
  const putDataTimeout = React.useRef();

  
  // debounce api requests to update user data
  const putUserData = (data) => {
    clearTimeout(putDataTimeout.current);
    putDataTimeout.current = setTimeout(() => {
      axios
        .put('/account/data', { data: JSON.stringify(data) }, { bypassErrorHandling: true })
        .catch(e => console.error(e));  // do nothing if failed to save to server
    }, 500);
  }


  // takes JSON data and saves it to user data
  const setUserData = React.useCallback((userData) => {
    setState(p => {
      // merge existing with potentially partial userData
      const newData = {...p.userData, ...userData};

      // clear undefined properties
      Object.keys(newData).forEach(key => newData[key] === undefined && delete newData[key]);
      putUserData(newData);

      return {
        ...p,
        userData: newData
      };
    });
  }, []);


  const setUserTheme = React.useCallback((themeId) => {
    setTheme(themeId);
    setUserData({ theme: { isDarkMode: themeId === Types.Theme.DARK }});
  }, [setTheme, setUserData]);


  // re-set user data when data property changes.
  // this happens when the user object changes and it passes the updated data via the property. 
  React.useEffect(() => {
    setState(p => ({ ...p, userData: data }));
    setTheme(data?.theme?.isDarkMode ? Types.Theme.DARK : Types.Theme.LIGHT);
  }, [data, setTheme]);


  const value = React.useMemo(() => ({
    ...state,
    setUserData,
    setUserTheme,
  }), [
    state,
    setUserData,
    setUserTheme,
  ]);

  
  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}